import axios from "axios";
import {refreshAccessToken} from "./api-user-service";

const instance = axios.create({
    baseURL: process.env.REACT_APP_SERVER_URL + "/api/curatorial-groups",
    headers: {
        "Content-Type": "application/json",
    },
});

instance.interceptors.request.use(
    (config: any) => {
        const token = getAccessToken();
        if (token) {
            config.headers["Authorization"] = "Bearer " + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instance.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {
        const originalConfig = err.config;
        if (err.response) {
            // Validation failed, ...
            if (err.response.status === 400 && err.response.data) {
                return Promise.reject(err.response.data);
            }
            // Access Token was expired
            if (
                err.response.status === 401 &&
                !originalConfig._retry &&
                getAccessToken() != null
            ) {
                originalConfig._retry = true;
                try {
                    const rs = await refreshAccessToken();
                    const { accessToken, refreshToken } = rs.data;
                    setRefreshToken(refreshToken);
                    setAccessToken(accessToken);
                    instance.defaults.headers.common["Authorization"] =
                        "Bearer " + accessToken;
                    return instance(originalConfig);
                } catch (_error: any) {
                    if (_error.response && _error.response.data) {
                        return Promise.reject(_error.response.data);
                    }
                    return Promise.reject(_error);
                }
            }
            if (err.response.status === 403 && err.response.data) {
                return Promise.reject(err.response.data);
            }
            if (err.response.status === 404) {
                if (axios.isAxiosError(err)) {
                    return Promise.reject(err.response.data);
                }
                return;
            }
        }
        return Promise.reject(err);
    }
);

export function setAccessToken(token: string) {
    window.localStorage.setItem("accessToken", token);
}

export function setRefreshToken(token: string) {
    window.localStorage.setItem("refreshToken", token);
}

export function getAccessToken(): null | string {
    const accessToken = window.localStorage.getItem("accessToken");
    return accessToken;
}

export function getrefreshToken(): null | string {
    const refreshToken = window.localStorage.getItem("refreshToken");
    return refreshToken;
}

export function getSelectedUser(): null | any {
    const selectedUserId = window.localStorage.getItem("selectedUser");
    return selectedUserId;
}

export function removeTokens() {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
}

const responseBody: any = (response: any) => response.data;

const request = {
    get: (url: string) => instance.get(url).then(responseBody),
    post: (url: string, body?: any) => instance.post(url, body).then(responseBody),
    put: (url: string, body?: any) => instance.put(url, body).then(responseBody),
    delete: (url: string) => instance.delete(url).then(responseBody),
};


const CuratorialGroup = {
    GetGroups: () => request.get("/get-all"),
    GetGroupByUser: () => request.get("/get-by-user"),
    AddGroup: (params: any) => request.post("/add", params),
    DeleteGroupById: (id: number) => request.delete(`/delete-by-id?id=${id}`),
    SetCurator: (groupId: number, curatorId: string) =>
        request.put(`/${groupId}/curator/${curatorId}`),
    AddListener: (groupId: number, listenerId: string) =>
        request.put(`/${groupId}/listeners/${listenerId}`),
    RemoveListener: (groupId: number, listenerId: string) =>
        request.delete(`/${groupId}/listeners/${listenerId}`),
};

export async function GetGroupsAsync() {
    return await CuratorialGroup.GetGroups()
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function GetGroupByUserAsync() {
    return await CuratorialGroup.GetGroupByUser()
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function AddGroupAsync(params: any) {
    return await CuratorialGroup.AddGroup(params)
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function DeleteGroupByIdAsync(id: number) {
    return await CuratorialGroup.DeleteGroupById(id)
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function SetCuratorAsync(groupId: number, curatorId: string) {
    return await CuratorialGroup.SetCurator(groupId, curatorId)
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function AddListenerAsync(groupId: number, listenerId: string) {
    return await CuratorialGroup.AddListener(groupId, listenerId)
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}

export async function RemoveListenerAsync(groupId: number, listenerId: string) {
    return await CuratorialGroup.RemoveListener(groupId, listenerId)
        .then((response) => {
            return {response};
        })
        .catch((error) => {
            return error.response;
        });
}
